import { officialEmojiExpression } from '../libs/emojii-regexp/index.js'
import { InvalidOperationError } from './errors/InvalidOperationError'
import { ArgumentNullError } from './errors/ArgumentNullError'

function toDateFromEpoch(epoch: number): Date {
	//let utcEpoch = epoch + 60 * 1000 * (new Date().getTimezoneOffset());
	//let result = new Date(utcEpoch);
	//return result;
	const result = new Date(epoch)
	return result
}

function isJson(obj: unknown): obj is Record<string, unknown> {
	const result = Boolean(obj && obj.constructor === {}.constructor)
	return result
}

function cutFirstLine(source: string): string {
	const result = source.substring(source.indexOf('\n') + 2)
	return result
}

const monthAbbr = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec'
]

function unifyHoursOrMinutes(source: number): string | number {
	if (source < 10) {
		return `0${source}`
	}
	return source
}

function unofficialEmojiExpression(): RegExp {
	return /[\u2388\u000e]/g
}

function findClosingQuote(value: string, quoteStart: number): number {
	let result = -1
	let isEscaped = false
	for (let i = quoteStart + 1; i < value.length; i++) {
		const ch = value[i]
		if (isEscaped) {
			isEscaped = false
			continue
		}
		switch (ch) {
			case '\\':
				isEscaped = true
				continue
			case '"':
				result = i
				return result
		}
	}
	return result
}

function findObjectOrArrayEnd(value: string, jsonStart: number): number {
	let result = -1
	let depth = 0
	let inString = false
	let escapeNext = false
	for (let i = jsonStart; i < value.length; i++) {
		const ch = value[i]
		if (escapeNext) {
			escapeNext = false
			continue
		}
		switch (ch) {
			case '\\':
				escapeNext = true
				continue
			case '"':
				inString = !inString
				continue
		}
		if (inString) {
			continue
		}
		switch (ch) {
			case '[':
			case '{':
				depth++
				break
			case ']':
			case '}':
				depth--
				if (depth === 0) {
					result = i
					return result
				}
				break
		}
	}
	return result
}

export const utils = {
	createTextTrackingId(): string {
		const array = new Uint8Array(16)
		const e = crypto.getRandomValues(array)
		const result = utils.pt(e)
		return result
	},

	pt: function (e: any) {
		for (
			var t,
				n = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/',
				r = [],
				i = 0,
				o = e.length,
				a = o % 3,
				s = o - a;
			i < s;
		)
			(t = e[i] << 16),
				(t |= e[i + 1] << 8),
				(t |= e[i + 2]),
				r.push(n.charAt((t >>> 18) & 63)),
				r.push(n.charAt((t >>> 12) & 63)),
				r.push(n.charAt((t >>> 6) & 63)),
				r.push(n.charAt(63 & t)),
				(i += 3)
		switch (a) {
			case 2:
				;(t = e[i] << 16),
					(t |= e[i + 1] << 8),
					r.push(n.charAt((t >>> 18) & 63)),
					r.push(n.charAt((t >>> 12) & 63)),
					r.push(n.charAt((t >>> 6) & 63)),
					r.push('=')
				break
			case 1:
				;(t = e[i] << 16),
					r.push(n.charAt((t >>> 18) & 63)),
					r.push(n.charAt((t >>> 12) & 63)),
					r.push('='),
					r.push('=')
		}
		return r.join('')
	},

	toString(obj: unknown): string {
		let result = ''
		if (obj instanceof Error) {
			let current: Error | null = obj
			do {
				result += current.stack ?? ''
				const inner: Error | undefined = (
					current as Error & { InnerError?: Error }
				).InnerError
				if (inner) {
					current = inner
					result += '\n *** Inner error ***\n'
				} else {
					current = null
				}
			} while (current)
		} else if (isJson(obj)) {
			result = JSON.stringify(obj)
		} else {
			result = String(obj)
		}
		return result
	},

	timeConverter(timestamp: string | number): string {
		const unixTimeStamp = parseInt(String(timestamp))
		const result = new Date(unixTimeStamp).toLocaleString()
		return result
	},

	isJson: isJson,

	isDefined(value: unknown): boolean {
		const result = value !== null && value !== undefined
		return result
	},

	asObject(value: unknown): object | null {
		const result = typeof value === 'object' && value !== null ? value : null
		return result
	},

	asArray(value: unknown): unknown[] | null {
		const result = Array.isArray(value) ? value : null
		return result
	},

	isObject(value: unknown): boolean {
		const result = value !== null && typeof value === 'object'
		return result
	},

	cutFirstLine: cutFirstLine,

	randomUUID(): string {
		const result = crypto.randomUUID()
		return result
	},

	getStackTrace(): string {
		const error = new Error()
		const withoutErrorLine = cutFirstLine(error.stack ?? '')
		const result = cutFirstLine(withoutErrorLine)
		return result
	},

	isIterable(obj: unknown): obj is Iterable<unknown> {
		if (!obj) {
			return false
		}
		const result =
			typeof (obj as Iterable<unknown>)[Symbol.iterator] === 'function'
		return result
	},

	addDays(date: Date, days: number): Date {
		const hours = 24
		const minutes = 60
		const seconds = 60
		const dateEpoch = date.getTime() + days * hours * minutes * seconds * 1000
		const result = new Date(dateEpoch)
		return result
	},

	addMinutes(date: Date, minutes: number): Date {
		const seconds = 60
		const dateEpoch = date.getTime() + minutes * seconds * 1000
		const result = new Date(dateEpoch)
		return result
	},

	addSeconds(date: Date, seconds: number): Date {
		const dateEpoch = date.getTime() + seconds * 1000
		const result = new Date(dateEpoch)
		return result
	},

	dateTimeToString(source: Date): string {
		const result = `${source.getDate()} ${monthAbbr[source.getMonth()]} ${source.getFullYear()} ${unifyHoursOrMinutes(source.getHours())}:${unifyHoursOrMinutes(source.getMinutes())}`
		return result
	},

	toDateFromIso8651(source: string): Date | null {
		const number = Date.parse(source)
		if (isNaN(number)) {
			return null
		}

		const result = new Date(number)
		return result
	},

	toDateFromEpoch(epoch: number): Date {
		const result = toDateFromEpoch(epoch)
		return result
	},

	getCurrentDateTime(): Date {
		const currentEpoch = Date.now()
		const result = toDateFromEpoch(currentEpoch)
		return result
	},

	toEpochFromDate(date: Date): number {
		const result = date.getTime()
		return result
	},

	clone<T>(source: T): T {
		const str = JSON.stringify(source)
		const result = JSON.parse(str)
		return result
	},

	cutOffEmoji(input: string | null | undefined): string | null | undefined {
		if (!input) {
			return input
		}

		//let oldExpression = /\p{Emoji}+/gu;
		const result = input
			.replace(officialEmojiExpression(), '')
			.replace(unofficialEmojiExpression(), '')
		return result
	},

	cutEdges(str: string): string {
		const result = str.substring(1, str.length - 1)
		return result
	},

	getChromeVersion(): string {
		try {
			const match = /Chrome\/([0-9.]+)/.exec(navigator.userAgent)
			const result = match![1] ?? '<unknown>'
			return result
		} catch {
			return '<unknown>'
		}
	},

	base64ToArrayBuffer(base64: string): ArrayBuffer {
		const binaryString = atob(base64)
		const bytes = new Uint8Array(binaryString.length)
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i)
		}
		return bytes.buffer
	},

	customEncodeUri(source: string): string {
		let result = encodeURIComponent(source)
		result = result.replace(/\(/g, '%28')
		result = result.replace(/\)/g, '%29')
		return result
	},

	customDecodeUri(source: string): string {
		const result = decodeURIComponent(source)
		return result
	},

	castToBoolean(variable: unknown, metadata?: unknown): boolean | null {
		if (typeof variable == 'boolean') {
			return variable
		}
		if (variable === undefined) {
			return null
		}
		throw new InvalidOperationError(
			`Variable is not boolean ${variable}`,
			metadata
		)
	},

	getReferrer(url: URL | { toString(): string; hash: string }): string {
		const result = url.toString().replace(url.hash, '')
		return result
	},

	single<T>(source: T[] | null | undefined, metadata?: unknown): T {
		if (!source) {
			throw new ArgumentNullError('Source is null.', metadata)
		}
		if (source.length === 0) {
			throw new InvalidOperationError('The input sequence is empty.', metadata)
		}
		if (source.length > 1) {
			throw new InvalidOperationError(
				'The input sequence contains more than one element.',
				metadata
			)
		}
		return source[0]
	},

	isEmptyObject(source: unknown): boolean {
		if (!source) {
			return false
		}

		const result = Object.getOwnPropertyNames(source).length === 0
		return result
	},

	findJsonEnd(value: string, jsonStart: number): number {
		let result
		if (value[jsonStart] === '"') {
			result = findClosingQuote(value, jsonStart)
			return result
		}

		result = findObjectOrArrayEnd(value, jsonStart)
		return result
	}
}
